/**
 * API Client with Bearer Token Authentication
 * 
 * Centralized API client that automatically injects access tokens
 * and handles authentication errors (401/403)
 */

import axios from 'axios';
import { msalInstance } from './msalInstance';
import { apiRequest } from './authConfig';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

// Create axios instance with base configuration
const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:7071/api',
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Token cache to prevent redundant token acquisition
let tokenCache = {
    token: null,
    expiresAt: null,
};
let interactiveTokenPromise = null;
let tokenFailureCooldownUntil = 0;

/**
 * Get access token for API calls
 * Attempts silent acquisition, falls back to redirect if needed
 * Uses in-memory cache to prevent redundant MSAL calls
 */
export const getAccessToken = async (forceRefresh = false) => {
    // Check if we have a cached token that's still valid (with 5-minute buffer)
    const now = Date.now();
    const bufferMs = 5 * 60 * 1000; // 5 minutes

    if (tokenFailureCooldownUntil > now) {
        throw new Error('Token acquisition temporarily unavailable. Please retry shortly.');
    }

    if (!forceRefresh && tokenCache.token && tokenCache.expiresAt && (tokenCache.expiresAt - bufferMs) > now) {
        console.log('[API Client] Using cached token');
        return tokenCache.token;
    }

    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
        console.error('[API Client] No authenticated accounts found');
        throw new Error('No authenticated account');
    }

    try {
        const request = {
            ...apiRequest,
            account: accounts[0],
            forceRefresh: forceRefresh
        };

        const response = await msalInstance.acquireTokenSilent(request);

        // Cache the token
        tokenCache.token = response.accessToken;
        tokenCache.expiresAt = response.expiresOn ? response.expiresOn.getTime() : now + (60 * 60 * 1000);

        console.log('[API Client] Token acquired and cached, expires at:', new Date(tokenCache.expiresAt));
        return response.accessToken;

    } catch (error) {
        // Clear cache on error
        tokenCache.token = null;
        tokenCache.expiresAt = null;

        const errorCode = String(error?.errorCode || '').toLowerCase();
        const isInteractiveError =
            error instanceof InteractionRequiredAuthError ||
            errorCode.includes('interaction_required') ||
            errorCode.includes('login_required') ||
            errorCode.includes('consent_required') ||
            errorCode.includes('timed_out') ||
            errorCode.includes('monitor_window_timeout');

        if (isInteractiveError) {
            console.warn('[API Client] Silent token acquisition failed, attempting popup authentication');

            if (interactiveTokenPromise) {
                return interactiveTokenPromise;
            }

            interactiveTokenPromise = msalInstance.acquireTokenPopup({
                ...apiRequest,
                account: accounts[0],
            }).then((popupResponse) => {
                tokenCache.token = popupResponse.accessToken;
                tokenCache.expiresAt = popupResponse.expiresOn
                    ? popupResponse.expiresOn.getTime()
                    : Date.now() + (60 * 60 * 1000);

                console.log('[API Client] Token acquired via popup and cached');
                return popupResponse.accessToken;
            }).catch((popupError) => {
                tokenFailureCooldownUntil = Date.now() + 15000;
                console.error('[API Client] Popup authentication failed:', popupError);
                throw popupError;
            }).finally(() => {
                interactiveTokenPromise = null;
            });

            return interactiveTokenPromise;
        }

        tokenFailureCooldownUntil = Date.now() + 10000;
        console.error('[API Client] Token acquisition failed:', error);
        throw error;
    }
};

/**
 * Request interceptor to inject Bearer token
 */
apiClient.interceptors.request.use(
    async (config) => {
        try {
            const token = await getAccessToken();

            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
                // Only log in development
                if (process.env.NODE_ENV === 'development') {
                    console.log('[API Client] Request authenticated');
                }
            }
        } catch (error) {
            console.error('[API Client] Failed to acquire token for request:', error);
            return Promise.reject(error);
        }

        return config;
    },
    (error) => {
        console.error('[API Client] Request interceptor error:', error);
        return Promise.reject(error);
    }
);

/**
 * Response interceptor to handle authentication errors
 */
apiClient.interceptors.response.use(
    (response) => {
        // Pass through successful responses
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
            console.error('[API Client] 401 Unauthorized - Token may be invalid or expired');

            // Clear token cache
            tokenCache.token = null;
            tokenCache.expiresAt = null;

            // Avoid infinite retry loop
            if (originalRequest._retry) {
                console.error('[API Client] Retry already attempted, not retrying again');
                // Do NOT call loginRedirect — it causes a full page reload
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            try {
                // Attempt to acquire new token (will fetch fresh since cache is cleared)
                const token = await getAccessToken(true);

                if (token) {
                    // Retry original request with new token
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return apiClient(originalRequest);
                }
            } catch (retryError) {
                console.error('[API Client] Token refresh failed:', retryError);
                return Promise.reject(retryError);
            }
        }

        // Handle 403 Forbidden (insufficient permissions)
        if (error.response?.status === 403) {
            console.error('[API Client] 403 Forbidden - Insufficient permissions');

            // You could show a custom error message or redirect to an error page
            error.message = 'You do not have permission to access this resource';
        }

        // Handle network errors
        if (!error.response) {
            console.error('[API Client] Network error:', error.message);
            error.message = 'Network error. Please check your connection.';
        }

        return Promise.reject(error);
    }
);

/**
 * API Client methods
 */
export const api = {
    /**
     * GET request
     */
    get: async (url, config = {}) => {
        try {
            const response = await apiClient.get(url, config);
            return response.data;
        } catch (error) {
            console.error('[API Client] GET error:', error);
            throw error;
        }
    },

    /**
     * POST request
     */
    post: async (url, data = {}, config = {}) => {
        try {
            const response = await apiClient.post(url, data, config);
            return response.data;
        } catch (error) {
            console.error('[API Client] POST error:', error);
            throw error;
        }
    },

    /**
     * PUT request
     */
    put: async (url, data = {}, config = {}) => {
        try {
            const response = await apiClient.put(url, data, config);
            return response.data;
        } catch (error) {
            console.error('[API Client] PUT error:', error);
            throw error;
        }
    },

    /**
     * DELETE request
     */
    delete: async (url, config = {}) => {
        try {
            const response = await apiClient.delete(url, config);
            return response.data;
        } catch (error) {
            console.error('[API Client] DELETE error:', error);
            throw error;
        }
    },

    /**
     * PATCH request
     */
    patch: async (url, data = {}, config = {}) => {
        try {
            const response = await apiClient.patch(url, data, config);
            return response.data;
        } catch (error) {
            console.error('[API Client] PATCH error:', error);
            throw error;
        }
    },
};

/**
 * Clear token cache (call on logout)
 */
/**
 * Headers for raw fetch/axios calls that must carry the Entra bearer token.
 * If the session has no token, the request still goes out and protected routes return 401.
 */
export async function authHeaders(extra = {}) {
    const headers = { ...extra };
    try {
        const token = await getAccessToken();
        if (token) headers.Authorization = `Bearer ${token}`;
    } catch (error) {
        console.warn('[API Client] Proceeding without a bearer token:', error?.message || error);
    }
    return headers;
}

export const clearTokenCache = () => {
    tokenCache.token = null;
    tokenCache.expiresAt = null;
    console.log('[API Client] Token cache cleared');
};

export default apiClient;
